import { useCallback, useEffect, useRef, useState } from 'react'
import {
	getLastSelectionFinder,
	parseJourneyArray,
} from '../../utils/journeyResumeUtils'

export function useHorizontalSlider() {
	const trackRef = useRef(null)
	const [canPrev, setCanPrev] = useState(false)
	const [canNext, setCanNext] = useState(false)

	const update = useCallback(() => {
		const el = trackRef.current
		if (!el) {
			setCanPrev(false)
			setCanNext(false)
			return
		}
		const max = el.scrollWidth - el.clientWidth
		setCanPrev(el.scrollLeft > 4)
		setCanNext(el.scrollLeft < max - 4)
	}, [])

	useEffect(() => {
		const el = trackRef.current
		if (!el) return undefined
		update()
		el.addEventListener('scroll', update, { passive: true })
		window.addEventListener('resize', update)
		return () => {
			el.removeEventListener('scroll', update)
			window.removeEventListener('resize', update)
		}
	}, [update])

	const scrollByCard = useCallback(direction => {
		const el = trackRef.current
		if (!el) return
		const amount = Math.max(280, Math.floor(el.clientWidth * 0.7))
		el.scrollBy({ left: direction * amount, behavior: 'smooth' })
	}, [])

	return {
		trackRef,
		canPrev,
		canNext,
		scrollPrev: () => scrollByCard(-1),
		scrollNext: () => scrollByCard(1),
		refresh: update,
	}
}

export function getJourneyCountries(journey) {
	return parseJourneyArray(journey?.countries)
}

export function getJourneyCareers(journey) {
	return parseJourneyArray(journey?.careers)
}

export function getJourneyCourses(journey) {
	return parseJourneyArray(journey?.courses)
}

export function getStudentJourneyCareer(journey) {
	const fromList = parseJourneyArray(journey?.careers)
	if (fromList.length) return fromList[0]
	return String(journey?.career || '').trim()
}

export function getStudentJourneyCourse(journey) {
	const fromList = parseJourneyArray(journey?.courses)
	if (fromList.length) return fromList[0]
	return String(journey?.course || '').trim()
}

export function hasConsultantJourneyProgress(journey) {
	return (
		getJourneyCareers(journey).length > 0 ||
		getJourneyCountries(journey).length > 0 ||
		getJourneyCourses(journey).length > 0
	)
}

export function hasStudentJourneyProgress(journey) {
	return (
		getJourneyCountries(journey).length > 0 ||
		Boolean(getStudentJourneyCareer(journey)) ||
		Boolean(getStudentJourneyCourse(journey))
	)
}

export function formatJourneyUpdated(journey) {
	const when =
		journey?.updated_at ||
		journey?.updatedAt ||
		journey?.created_at ||
		journey?.createdAt
	if (!when) return null
	const date = new Date(when)
	if (Number.isNaN(date.getTime())) return null
	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	})
}

export function consultantJourneyTitle(journey) {
	const countries = getJourneyCountries(journey)
	const careers = getJourneyCareers(journey)
	const courses = getJourneyCourses(journey)
	if (countries[0] && careers[0]) return `${countries[0]} · ${careers[0]}`
	if (careers[0] && courses[0]) return `${careers[0]} · ${courses[0]}`
	if (countries[0]) return `${countries[0]} Explorer`
	if (careers[0]) return careers[0]
	if (courses[0]) return courses[0]
	return 'Saved research'
}

export function navigateFromJourney(journey, setActiveScreen) {
	setActiveScreen(getLastSelectionFinder(journey))
}
